import { useMemo } from "react";
import { EMPLOYEE_FIELDS } from "../employeeFieldSchema";
import { SYSTEM_ROLES } from "../employeeRoleConstants";
import { coerceEmployeeRecord, validateEmployeeRecord } from "../employeeValidation";

function pill(kind) {
  if (kind === "ok") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (kind === "warn") return "bg-amber-50 text-amber-800 border-amber-200";
  if (kind === "bad") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
}

function SelectField({ label, value, onChange, options, error, required, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        {label}
        {required ? <span className="text-amber-600"> *</span> : null}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
          error ? "border-amber-300" : "border-slate-200"
        }`}
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      {hint ? <p className="text-[11px] text-indigo-600 mt-1 font-medium">{hint}</p> : null}
      {error ? <p className="text-xs text-amber-700 mt-1">{error}</p> : null}
    </div>
  );
}

function TextField({ label, value, onChange, error, required, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        {label}
        {required ? <span className="text-amber-600"> *</span> : null}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
          error ? "border-amber-300" : "border-slate-200"
        }`}
      />
      {error ? <p className="text-xs text-amber-700 mt-1">{error}</p> : null}
    </div>
  );
}

function employeeLabel(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || row.work_email || row.email || "Unnamed employee";
}

export function validateBulkEmployeeRows(rows, lookups) {
  const rowErrors = [];
  let completed = 0;
  for (let i = 0; i < rows.length; i++) {
    const coerced = coerceEmployeeRecord(rows[i], { lookups });
    const errs = validateEmployeeRecord(coerced, { mode: "bulk", lookups });
    if (Object.keys(errs).length === 0) completed += 1;
    else {
      rowErrors.push({
        row: i + 1,
        rowIndex: i,
        fieldErrors: errs,
        error: Object.entries(errs)
          .slice(0, 4)
          .map(([k, v]) => `${k}: ${v}`)
          .join(" | "),
      });
    }
  }
  return {
    rowErrors,
    completed,
    total: rows.length,
    allValid: completed === rows.length && rows.length > 0,
  };
}

// Collect distinct non-blank values for a key across rows (case-insensitive de-dup, keeps first casing)
function collectDistinct(rows, key) {
  const seen = new Map();
  for (const r of rows || []) {
    const raw = r?.[key];
    if (raw === null || raw === undefined) continue;
    const s = String(raw).trim();
    if (!s) continue;
    const lower = s.toLowerCase();
    if (!seen.has(lower)) seen.set(lower, s);
  }
  return Array.from(seen.values());
}

// Build merged option list: existing DB items + file-only names (marked as "from file")
function buildMergedOptions({ lookupItems, fileNames }) {
  const existingByName = new Map();
  const options = [];
  for (const item of lookupItems || []) {
    const name = String(item?.name ?? "").trim();
    if (!name) continue;
    existingByName.set(name.toLowerCase(), item);
    options.push({ value: String(item.id), label: name, source: "db" });
  }
  const fileOnly = (fileNames || []).filter((n) => !existingByName.has(n.toLowerCase()));
  if (fileOnly.length) {
    // Separator (disabled)
    options.push({ value: "__sep__", label: "── From uploaded file (not yet in system) ──", disabled: true });
    for (const name of fileOnly) {
      options.push({
        value: `__file__:${name}`,
        label: `${name}  (new from file)`,
        source: "file",
      });
    }
  }
  return { options, fileOnly };
}

function SourceChip({ kind, count }) {
  const styles = kind === "db"
    ? { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", dot: "bg-indigo-500" }
    : { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", dot: "bg-amber-500" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${styles.bg} ${styles.text} ${styles.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${styles.dot}`}></span>
      {count}
    </span>
  );
}

function SourceSummaryCard({ icon, title, existing, fromFile, existingNames, fileOnlyNames }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center font-bold text-sm">
            {icon}
          </div>
          <p className="text-sm font-bold text-slate-800">{title}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <SourceChip kind="db" count={`${existing} existing`} />
          {fromFile > 0 ? <SourceChip kind="file" count={`+${fromFile} new`} /> : null}
        </div>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {existingNames.slice(0, 8).map((n) => (
          <span key={`db-${n}`} className="text-[11px] px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
            {n}
          </span>
        ))}
        {existingNames.length > 8 ? (
          <span className="text-[11px] px-2 py-0.5 text-slate-400">+{existingNames.length - 8} more</span>
        ) : null}
        {fileOnlyNames.slice(0, 8).map((n) => (
          <span key={`file-${n}`} className="text-[11px] px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 font-semibold">
            + {n}
          </span>
        ))}
        {fileOnlyNames.length > 8 ? (
          <span className="text-[11px] px-2 py-0.5 text-slate-400">+{fileOnlyNames.length - 8} more</span>
        ) : null}
      </div>
    </div>
  );
}

export default function BulkEmployeeRequirementsStep({
  rows,
  lookups,
  onRowsChange,
  onBack,
  onContinue,
}) {
  // Distinct file values per FK
  const fileDeptNames = useMemo(() => collectDistinct(rows, "department_name"), [rows]);
  const fileDesigNames = useMemo(() => collectDistinct(rows, "designation_name"), [rows]);
  const fileLocNames = useMemo(() => collectDistinct(rows, "location_name"), [rows]);

  // Merged option lists (existing + from file)
  const deptMerged = useMemo(
    () => buildMergedOptions({ lookupItems: lookups?.departments, fileNames: fileDeptNames }),
    [lookups, fileDeptNames]
  );
  const desigMerged = useMemo(
    () => buildMergedOptions({ lookupItems: lookups?.designations, fileNames: fileDesigNames }),
    [lookups, fileDesigNames]
  );
  const locMerged = useMemo(
    () => buildMergedOptions({ lookupItems: lookups?.locations, fileNames: fileLocNames }),
    [lookups, fileLocNames]
  );

  const deptOptions = deptMerged.options;
  const desigOptions = desigMerged.options;
  const locOptions = locMerged.options;

  const roleOptions = useMemo(
    () => SYSTEM_ROLES.map((r) => ({ value: r.value, label: r.label })),
    []
  );
  const genderOptions = useMemo(() => {
    const f = EMPLOYEE_FIELDS.find((x) => x.key === "gender");
    return (f?.enumValues || []).map((g) => ({ value: g, label: g }));
  }, []);
  const typeOptions = useMemo(() => {
    const f = EMPLOYEE_FIELDS.find((x) => x.key === "employee_type");
    return (f?.enumValues || []).map((t) => ({ value: t, label: t }));
  }, []);

  const validation = useMemo(() => validateBulkEmployeeRows(rows, lookups), [rows, lookups]);

  const updateRow = (index, patch) => {
    onRowsChange((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  // Returns the value to show as "selected" in the dropdown for an FK field on a row.
  // If the row has an existing ID -> use it.
  // Else if the row has a `*_name` matching an existing lookup -> match by name.
  // Else if the row has a `*_name` from the file -> show as "__file__:Name".
  const resolveFkValue = (row, idKey, nameKey, lookupItems) => {
    const id = row?.[idKey];
    if (id && String(id).trim()) return String(id);
    const nm = row?.[nameKey];
    if (!nm) return "";
    const lower = String(nm).trim().toLowerCase();
    const hit = (lookupItems || []).find((x) => String(x?.name ?? "").trim().toLowerCase() === lower);
    if (hit) return String(hit.id);
    return `__file__:${String(nm).trim()}`;
  };

  // Apply user dropdown choice — either an existing ID, blank, or a __file__ marker.
  const setFkValue = (idx, idKey, nameKey, value) => {
    if (!value) {
      updateRow(idx, { [idKey]: "", [nameKey]: "" });
      return;
    }
    if (value.startsWith("__file__:")) {
      const name = value.slice("__file__:".length);
      updateRow(idx, { [idKey]: "", [nameKey]: name });
      return;
    }
    updateRow(idx, { [idKey]: value, [nameKey]: "" });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h4 className="text-lg font-bold text-slate-900">Complete employee requirements</h4>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Every imported employee must have department, designation, location, role, and all other
            required fields before they are created. Pick an existing entry, or use the value taken from
            your file (shown in amber).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${pill(validation.allValid ? "ok" : "warn")}`}>
            {validation.completed} / {validation.total} ready
          </span>
        </div>
      </div>

      {/* Source summary: existing DB entries + entries found in uploaded file */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SourceSummaryCard
          icon="D"
          title="Departments"
          existing={(lookups?.departments || []).length}
          fromFile={deptMerged.fileOnly.length}
          existingNames={(lookups?.departments || []).map((d) => d.name)}
          fileOnlyNames={deptMerged.fileOnly}
        />
        <SourceSummaryCard
          icon="J"
          title="Designations"
          existing={(lookups?.designations || []).length}
          fromFile={desigMerged.fileOnly.length}
          existingNames={(lookups?.designations || []).map((d) => d.name)}
          fileOnlyNames={desigMerged.fileOnly}
        />
        <SourceSummaryCard
          icon="L"
          title="Locations"
          existing={(lookups?.locations || []).length}
          fromFile={locMerged.fileOnly.length}
          existingNames={(lookups?.locations || []).map((d) => d.name)}
          fileOnlyNames={locMerged.fileOnly}
        />
      </div>

      {(deptMerged.fileOnly.length + desigMerged.fileOnly.length + locMerged.fileOnly.length) > 0 ? (
        <div className="mt-3 p-3.5 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900 flex items-start gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div>
            Your file contains values that do not yet exist in the system (shown in amber).
            Either pick a matching existing entry below, or create the missing department/designation/location
            first from the setup pages, then come back here.
          </div>
        </div>
      ) : null}

      {!validation.allValid ? (
        <div className="mt-3 p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
          <b>Mandatory:</b> Fill all required fields for each employee below. You cannot create employees
          until every row shows <b>Complete</b>.
        </div>
      ) : (
        <div className="mt-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
          All {validation.total} employees are complete. Continue to final review and create.
        </div>
      )}

      <div className="mt-5 space-y-4">
        {rows.map((row, idx) => {
          const rowErr = validation.rowErrors.find((e) => e.rowIndex === idx);
          const complete = !rowErr;

          const deptValue = resolveFkValue(row, "department_id", "department_name", lookups?.departments);
          const desigValue = resolveFkValue(row, "designation_id", "designation_name", lookups?.designations);
          const locValue = resolveFkValue(row, "location_id", "location_name", lookups?.locations);

          const deptHint = row.department_name ? `From file: ${row.department_name}` : null;
          const desigHint = row.designation_name ? `From file: ${row.designation_name}` : null;
          const locHint = row.location_name ? `From file: ${row.location_name}` : null;

          return (
            <div
              key={idx}
              className={`rounded-xl border p-4 md:p-5 transition-colors ${
                complete ? "border-slate-200 bg-slate-50/30" : "border-amber-200 bg-amber-50/40"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Employee {idx + 1}
                  </p>
                  <p className="text-base font-bold text-slate-900">{employeeLabel(row)}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {row.work_email || row.email || "No email yet"}
                  </p>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${pill(complete ? "ok" : "bad")}`}>
                  {complete ? "Complete" : "Incomplete"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <SelectField
                  label="Department"
                  required
                  value={deptValue}
                  options={deptOptions}
                  error={rowErr?.fieldErrors?.department_id}
                  hint={deptHint}
                  onChange={(v) => setFkValue(idx, "department_id", "department_name", v)}
                />
                <SelectField
                  label="Designation"
                  required
                  value={desigValue}
                  options={desigOptions}
                  error={rowErr?.fieldErrors?.designation_id}
                  hint={desigHint}
                  onChange={(v) => setFkValue(idx, "designation_id", "designation_name", v)}
                />
                <SelectField
                  label="Location"
                  required
                  value={locValue}
                  options={locOptions}
                  error={rowErr?.fieldErrors?.location_id}
                  hint={locHint}
                  onChange={(v) => setFkValue(idx, "location_id", "location_name", v)}
                />
                <SelectField
                  label="System role"
                  required
                  value={row.role}
                  options={roleOptions}
                  error={rowErr?.fieldErrors?.role}
                  onChange={(v) => updateRow(idx, { role: v })}
                />
                <SelectField
                  label="Employee type"
                  required
                  value={row.employee_type}
                  options={typeOptions}
                  error={rowErr?.fieldErrors?.employee_type}
                  onChange={(v) => updateRow(idx, { employee_type: v })}
                />
                <SelectField
                  label="Gender"
                  required
                  value={row.gender}
                  options={genderOptions}
                  error={rowErr?.fieldErrors?.gender}
                  onChange={(v) => updateRow(idx, { gender: v })}
                />
                <TextField
                  label="Work email"
                  required
                  value={row.work_email || row.email}
                  error={rowErr?.fieldErrors?.work_email}
                  onChange={(v) => updateRow(idx, { work_email: v, email: v })}
                />
                <TextField
                  label="First name"
                  required
                  value={row.first_name}
                  error={rowErr?.fieldErrors?.first_name}
                  onChange={(v) => updateRow(idx, { first_name: v })}
                />
                <TextField
                  label="Date of birth"
                  required
                  value={row.date_of_birth}
                  error={rowErr?.fieldErrors?.date_of_birth}
                  onChange={(v) => updateRow(idx, { date_of_birth: v })}
                />
                <TextField
                  label="Date of joining"
                  required
                  value={row.date_of_joining}
                  error={rowErr?.fieldErrors?.date_of_joining}
                  onChange={(v) => updateRow(idx, { date_of_joining: v })}
                />
                <TextField
                  label="Annual CTC"
                  required
                  type="number"
                  value={row.annual_ctc}
                  error={rowErr?.fieldErrors?.annual_ctc}
                  onChange={(v) => updateRow(idx, { annual_ctc: v })}
                />
              </div>

              {rowErr && !complete ? (
                <p className="mt-3 text-xs text-amber-700 font-medium">{rowErr.error}</p>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          ← Back to import preview
        </button>
        <button
          type="button"
          disabled={!validation.allValid}
          onClick={onContinue}
          className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold hover:from-indigo-700 hover:to-violet-700 shadow-md shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          Continue to final review ({validation.completed}/{validation.total})
        </button>
      </div>
    </div>
  );
}
